import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { FASE_LABELS, PLANO_LABELS, STATUS_LABELS, brl, statusColor } from "@/lib/format";
import { Plus, Search, X, Upload, Trash2, Send, AlertTriangle } from "lucide-react";

const SPECIAL_FILTERS = {
    vencimento_60d: { label: "Vencendo em 60 dias", testid: "filter-vencimento_60d" },
    renovacao_60d: { label: "Renovações em 60 dias", testid: "filter-renovacao_60d" },
    a_contatar_mes: { label: "A contatar este mês", testid: "filter-a_contatar_mes" },
    sem_interacao: { label: "Sem interação no prazo", testid: "filter-sem_interacao" },
    criticos_atencao: { label: "Críticos e Atenção", testid: "filter-criticos_atencao" },
    status_critico: { label: "Saúde: Crítico", testid: "filter-status_critico" },
    status_atencao: { label: "Saúde: Atenção", testid: "filter-status_atencao" },
    status_saudavel: { label: "Saúde: Saudável", testid: "filter-status_saudavel" },
    status_excelente: { label: "Saúde: Excelente", testid: "filter-status_excelente" },
};

const STATUS_SPECIAL = {
    status_critico: "critico", status_atencao: "atencao",
    status_saudavel: "saudavel", status_excelente: "excelente",
};

const ESFERA_LABEL = { federal: "Federal", estadual: "Estadual", municipal: "Municipal" };

export default function ContractsList() {
    const { user } = useAuth();
    const isMaster = user?.role === "master";
    const [items, setItems] = useState([]);
    const [extraData, setExtraData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [filterPlano, setFilterPlano] = useState("all");
    const [filterStatus, setFilterStatus] = useState("all");
    const [filterFase, setFilterFase] = useState("all");
    const [filterVendedor, setFilterVendedor] = useState("all");
    const [filterEsfera, setFilterEsfera] = useState("all");
    const [sortKey, setSortKey] = useState("score");
    const [deleting, setDeleting] = useState(null);        // M1 — contrato em confirmação de exclusão
    const [encaminhando, setEncaminhando] = useState(null); // M14 — contrato p/ registrar encaminhamento
    const [searchParams, setSearchParams] = useSearchParams();
    const specialFilter = searchParams.get("filter") || null;

    async function reload() {
        setLoading(true);
        try {
            const [c, k] = await Promise.all([api.get("/contracts"), api.get("/dashboard/kpis-extra")]);
            setItems(c.data); setExtraData(k.data);
        } finally { setLoading(false); }
    }

    useEffect(() => { reload(); }, []);

    const vendedorList = useMemo(() => Array.from(new Set(items.map(i => i.vendedor_responsavel || i.cs_responsavel))).filter(Boolean), [items]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        let baseIds = null;
        if (specialFilter && extraData) {
            if (specialFilter === "vencimento_60d" || specialFilter === "renovacao_60d") baseIds = new Set(extraData.vencimento_60d.contract_ids);
            else if (specialFilter === "a_contatar_mes") baseIds = new Set(extraData.a_contatar_mes.contract_ids);
            else if (specialFilter === "sem_interacao") baseIds = new Set(extraData.sem_interacao.contract_ids);
        }
        // M15 — filtro de status crítico/atenção vindo do KPI do Dashboard
        if (specialFilter === "criticos_atencao") {
            baseIds = new Set(items.filter(c => c.status === "critico" || c.status === "atencao").map(c => c.id));
        }
        // M5 — filtro por status único vindo do gráfico "Saúde dos contratos"
        if (STATUS_SPECIAL[specialFilter]) {
            const st = STATUS_SPECIAL[specialFilter];
            baseIds = new Set(items.filter(c => c.status === st).map(c => c.id));
        }
        let out = items.filter((c) => {
            if (baseIds && !baseIds.has(c.id)) return false;
            if (filterPlano !== "all" && c.plano !== filterPlano) return false;
            if (filterStatus !== "all" && c.status !== filterStatus) return false;
            if (filterFase !== "all" && c.fase_atual !== filterFase) return false;
            const vend = c.vendedor_responsavel || c.cs_responsavel;
            if (filterVendedor !== "all" && vend !== filterVendedor) return false;
            if (filterEsfera !== "all" && c.esfera !== filterEsfera) return false;
            if (q && !c.organizacao_nome.toLowerCase().includes(q)) return false;
            return true;
        });
        const cmps = {
            score: (a, b) => a.score - b.score,
            name: (a, b) => a.organizacao_nome.localeCompare(b.organizacao_nome),
            fase: (a, b) => a.fase_atual.localeCompare(b.fase_atual),
            date: (a, b) => (a.proxima_acao_data || "9999").localeCompare(b.proxima_acao_data || "9999"),
        };
        return [...out].sort(cmps[sortKey] || cmps.score);
    }, [items, search, filterPlano, filterStatus, filterFase, filterVendedor, filterEsfera, sortKey, specialFilter, extraData]);

    function clearSpecial() {
        const sp = new URLSearchParams(searchParams);
        sp.delete("filter");
        setSearchParams(sp);
    }

    if (loading) return <div className="text-sm text-slate-500">Carregando contratos…</div>;

    return (
        <div className="space-y-6 fade-in" data-testid="contracts-page">
            <header className="flex items-center justify-between">
                <div>
                    <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-medium">Customer Success</div>
                    <h1 className="font-display font-bold text-3xl text-[#0F2044] mt-1">Contratos</h1>
                    <p className="text-sm text-slate-500 mt-1">{items.length} contratos · acompanhe a saúde, fase e próxima ação de cada cliente.</p>
                </div>
                <div className="flex items-center gap-2">
                    <Link to="/contratos/importar" data-testid="import-contracts-button" className="inline-flex items-center gap-2 border border-[#0F2044]/30 text-[#0F2044] hover:bg-[#0F2044]/5 text-sm font-semibold px-4 py-2.5 rounded-md transition-colors">
                        <Upload className="h-4 w-4" /> Importar em lote
                    </Link>
                    <Link to="/contratos/novo" data-testid="new-contract-button" className="inline-flex items-center gap-2 bg-[#0F2044] hover:bg-[#172E5E] text-white text-sm font-semibold px-4 py-2.5 rounded-md transition-colors">
                        <Plus className="h-4 w-4" /> Novo contrato
                    </Link>
                </div>
            </header>

            {specialFilter && SPECIAL_FILTERS[specialFilter] && (
                <div className="bg-[#0F2044]/5 border border-[#0F2044]/20 rounded-md px-4 py-2.5 flex items-center gap-3 text-sm" data-testid="special-filter-active">
                    <span className="text-[#0F2044] font-semibold">Filtro ativo:</span>
                    <span className="text-[#0F2044]">{SPECIAL_FILTERS[specialFilter].label} ({filtered.length})</span>
                    <button onClick={clearSpecial} data-testid="clear-special-filter" className="ml-auto text-xs text-slate-600 hover:text-[#0F2044] flex items-center gap-1">
                        <X className="h-3 w-3" /> Limpar
                    </button>
                </div>
            )}

            <div className="bg-white border border-slate-200 rounded-lg p-4 grid grid-cols-12 gap-3 items-end" data-testid="contracts-filters">
                <div className="col-span-3">
                    <label className="block text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1">Buscar</label>
                    <div className="relative">
                        <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                            type="text"
                            placeholder="Nome da organização"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            data-testid="filter-search-input"
                            className="w-full pl-9 pr-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#0F2044] focus:ring-1 focus:ring-[#0F2044]/30 focus:outline-none"
                        />
                    </div>
                </div>
                <Select label="Plano" value={filterPlano} onChange={setFilterPlano} testid="filter-plano"
                    options={[["all", "Todos"], ["premium", "Premium"], ["personalizado", "Personalizado"], ["especial", "Especial"]]} />
                <Select label="Esfera" value={filterEsfera} onChange={setFilterEsfera} testid="filter-esfera"
                    options={[["all", "Todas"], ["federal", "Federal"], ["estadual", "Estadual"], ["municipal", "Municipal"]]} />
                <Select label="Status" value={filterStatus} onChange={setFilterStatus} testid="filter-status"
                    options={[["all", "Todos"], ...["critico", "atencao", "saudavel", "excelente"].map(s => [s, STATUS_LABELS[s]])]} />
                <Select label="Fase" value={filterFase} onChange={setFilterFase} testid="filter-fase"
                    options={[["all", "Todas"], ...Object.entries(FASE_LABELS)]} />
                <Select label="Vendedor" value={filterVendedor} onChange={setFilterVendedor} testid="filter-vendedor"
                    options={[["all", "Todos"], ...vendedorList.map(c => [c, c])]} />
                <Select label="Ordenar por" value={sortKey} onChange={setSortKey} testid="filter-sort"
                    options={[["score", "Score (cresc.)"], ["name", "Nome (A-Z)"], ["fase", "Fase"], ["date", "Próxima ação"]]} />
            </div>

            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="contracts-table-card">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                        <tr className="text-left text-xs uppercase tracking-[0.12em] text-slate-500 border-b border-slate-200">
                            <th className="py-3 pl-4 pr-3 font-medium w-1"></th>
                            <th className="py-3 pr-3 font-medium">Cliente</th>
                            <th className="py-3 pr-3 font-medium">Plano</th>
                            <th className="py-3 pr-3 font-medium">Fase</th>
                            <th className="py-3 pr-3 font-medium text-right">Score</th>
                            <th className="py-3 pr-3 font-medium">Status</th>
                            <th className="py-3 pr-3 font-medium">Vendedor</th>
                            <th className="py-3 pr-3 font-medium">Próxima ação</th>
                            <th className="py-3 pr-4 font-medium">Prazo</th>
                            <th className="py-3 pr-4 font-medium text-right"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 && (
                            <tr><td colSpan={10} className="text-center text-slate-500 py-10">Nenhum contrato encontrado.</td></tr>
                        )}
                        {filtered.map((c) => (
                            <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`contract-row-${c.id}`}>
                                <td className="pl-4 pr-3 py-3">
                                    <span className="block h-7 w-1 rounded-r" style={{ background: statusColor(c.status) }} />
                                </td>
                                <td className="pr-3 py-3">
                                    <Link to={`/contratos/${c.id}`} className="font-medium text-[#0F2044] hover:underline" data-testid={`contract-link-${c.id}`}>
                                        {c.organizacao_nome}
                                    </Link>
                                    <div className="text-xs text-slate-500">{ESFERA_LABEL[c.esfera] || (c.esfera || "—")}</div>
                                </td>
                                <td className="pr-3 py-3 text-slate-700">{PLANO_LABELS[c.plano]}</td>
                                <td className="pr-3 py-3 text-slate-700">{FASE_LABELS[c.fase_atual]}</td>
                                <td className="pr-3 py-3 text-right font-mono-tab font-semibold text-[#0F2044]">{c.score}</td>
                                <td className="pr-3 py-3"><StatusBadge status={c.status} /></td>
                                <td className="pr-3 py-3 text-slate-700">{c.vendedor_responsavel || c.cs_responsavel || "—"}</td>
                                <td className="pr-3 py-3 text-slate-700 max-w-xs truncate" title={c.proxima_acao}>{c.proxima_acao || "—"}</td>
                                <td className="pr-4 py-3 font-mono-tab text-slate-700">{brl(c.proxima_acao_data)}</td>
                                <td className="pr-4 py-3">
                                    <div className="flex items-center justify-end gap-1.5">
                                        {(specialFilter === "vencimento_60d" || specialFilter === "renovacao_60d" || c.fase_atual === "renovacao") && (
                                            <button onClick={() => setEncaminhando(c)} data-testid={`encaminhar-btn-${c.id}`}
                                                title="Registrar encaminhamento de renovação"
                                                className="text-[11px] font-semibold border border-emerald-300 text-emerald-700 hover:bg-emerald-50 px-2 py-1 rounded-md inline-flex items-center gap-1">
                                                <Send className="h-3 w-3" /> Encaminhamento
                                            </button>
                                        )}
                                        {isMaster && (
                                            <button onClick={() => setDeleting(c)} data-testid={`delete-contract-btn-${c.id}`}
                                                title="Excluir organização (permanente)"
                                                className="p-1.5 rounded text-slate-300 hover:text-red-600 hover:bg-red-50 transition-colors">
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {deleting && (
                <DeleteContractModal contract={deleting} onClose={() => setDeleting(null)}
                    onDeleted={async () => { setDeleting(null); await reload(); }} />
            )}
            {encaminhando && (
                <EncaminhamentoModal contract={encaminhando} onClose={() => setEncaminhando(null)}
                    onSaved={async () => { setEncaminhando(null); await reload(); }} />
            )}
        </div>
    );
}

// M1 — modal de exclusão permanente (apenas Master)
function DeleteContractModal({ contract, onClose, onDeleted }) {
    const [confirmText, setConfirmText] = useState("");
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState("");
    const canDelete = confirmText.trim().toUpperCase() === "EXCLUIR";

    async function doDelete() {
        setDeleting(true); setError("");
        try {
            await api.delete(`/contracts/${contract.id}`);
            await onDeleted();
        } catch (e) {
            setError(e?.response?.data?.detail || "Falha ao excluir o contrato.");
            setDeleting(false);
        }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4" data-testid="delete-contract-modal">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 fade-in">
                <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                        <AlertTriangle className="h-5 w-5 text-red-600" />
                    </div>
                    <div className="flex-1">
                        <h3 className="font-display font-semibold text-lg text-[#0F2044]">Excluir organização?</h3>
                        <p className="text-sm text-slate-600 mt-1">
                            Você está prestes a excluir <strong>{contract.organizacao_nome}</strong>.
                        </p>
                        <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="delete-warning">
                            <strong>Aviso de exclusão permanente:</strong> jornada, reuniões, indicadores de uso, ações, treinamentos e todo o histórico serão apagados. Esta ação <strong>não pode ser desfeita</strong>.
                        </div>
                        <label className="block text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mt-4 mb-1">
                            Digite EXCLUIR para confirmar
                        </label>
                        <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                            placeholder="EXCLUIR" data-testid="delete-confirm-input"
                            className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm focus:border-red-500 focus:ring-1 focus:ring-red-500/30 focus:outline-none" />
                        {error && <div className="text-xs text-red-600 font-medium mt-2" data-testid="delete-error">{error}</div>}
                    </div>
                </div>
                <div className="flex justify-end gap-2 mt-5">
                    <button onClick={onClose} data-testid="delete-cancel" className="text-sm px-4 py-2 border border-slate-300 text-slate-700 rounded-md hover:bg-slate-50">Cancelar</button>
                    <button onClick={doDelete} disabled={!canDelete || deleting} data-testid="delete-confirm-button"
                        className="text-sm px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
                        <Trash2 className="h-4 w-4" /> {deleting ? "Excluindo…" : "Excluir permanentemente"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// M14 — modal de encaminhamento de renovação (histórico + ação)
function EncaminhamentoModal({ contract, onClose, onSaved }) {
    const [descricao, setDescricao] = useState("");
    const [prazo, setPrazo] = useState("");
    const [prioridade, setPrioridade] = useState("alta");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    async function submit() {
        if (!descricao.trim() || !prazo) { setError("Preencha a descrição e o prazo."); return; }
        setSaving(true); setError("");
        try {
            await api.post(`/contracts/${contract.id}/renovacao-encaminhamento`, {
                descricao: descricao.trim(), prazo, prioridade,
            });
            await onSaved();
        } catch (e) {
            setError(e?.response?.data?.detail || "Falha ao registrar o encaminhamento.");
            setSaving(false);
        }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4" data-testid="encaminhamento-modal">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 fade-in">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-600 font-semibold">Renovação</div>
                        <h3 className="font-display font-semibold text-lg text-[#0F2044] mt-1">Registrar encaminhamento</h3>
                        <div className="text-xs text-slate-500 mt-0.5">{contract.organizacao_nome} · vence em {brl(contract.data_vencimento)}</div>
                    </div>
                    <button onClick={onClose} data-testid="encaminhamento-close" className="text-slate-400 hover:text-slate-700 -mt-1">
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <div className="space-y-4">
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1">Descrição do encaminhamento *</label>
                        <textarea rows={3} value={descricao} onChange={(e) => setDescricao(e.target.value)}
                            placeholder="Ex.: Enviar proposta comercial de renovação e agendar reunião de check com o fiscal do contrato"
                            data-testid="encaminhamento-desc" autoFocus
                            className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#0F2044] focus:ring-1 focus:ring-[#0F2044]/30 focus:outline-none" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1">Prazo *</label>
                            <input type="date" value={prazo} onChange={(e) => setPrazo(e.target.value)} data-testid="encaminhamento-prazo"
                                className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#0F2044] focus:outline-none" />
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1">Prioridade</label>
                            <select value={prioridade} onChange={(e) => setPrioridade(e.target.value)} data-testid="encaminhamento-prioridade"
                                className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm bg-white focus:border-[#0F2044] focus:outline-none">
                                <option value="alta">Alta</option><option value="media">Média</option><option value="baixa">Baixa</option>
                            </select>
                        </div>
                    </div>
                    <p className="text-[11px] text-slate-500">Cria uma <strong>anotação no Histórico</strong> e uma <strong>ação no Plano de Ação</strong> do contrato.</p>
                    {error && <div className="text-xs text-red-600 font-medium" data-testid="encaminhamento-error">{error}</div>}
                </div>
                <div className="flex justify-end gap-2 mt-5">
                    <button onClick={onClose} data-testid="encaminhamento-cancel" className="text-sm px-4 py-2 border border-slate-300 text-slate-700 rounded-md hover:bg-slate-50">Cancelar</button>
                    <button onClick={submit} disabled={saving} data-testid="encaminhamento-submit"
                        className="text-sm px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md font-semibold disabled:opacity-60 inline-flex items-center gap-1.5">
                        <Send className="h-4 w-4" /> {saving ? "Registrando…" : "Registrar encaminhamento"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Select({ label, value, onChange, options, testid }) {
    return (
        <div className="col-span-2">
            <label className="block text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1">{label}</label>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm bg-white focus:border-[#0F2044] focus:ring-1 focus:ring-[#0F2044]/30 focus:outline-none"
            >
                {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
        </div>
    );
}
